import classnames from 'classnames';
import type { CSSProperties, ComponentProps } from 'react';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Captain } from 'components/so5/Captain';
import { unitMapping } from 'lib/style';

type Props = {
  active: boolean;
  onClick?: () => void;
  disablePositioning?: boolean;
  disableAnimation?: boolean;
} & Pick<ComponentProps<typeof Captain>, 'size'>;

const zIndexes = {
  outerContext: {
    aboveCard: 10, // make sure it goes above stats
  },
  innerContext: {
    belowLetter1: 1,
    belowLetter2: 2,
    letter: 3,
    aboveLetter: 4,
  },
};

const bounce = keyframes`
  from {
    transform: scale(1);
  }
  33% {
    transform: scale(1.15);
  }
  66% {
    transform: scale(1);
  }
  to {
    transform: scale(1);
  }
`;

const bounceIn = keyframes`
  from {
    transform: scale(0);
  }
  20% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  70% {
    transform: scale(1.05);
  }
  to {
    transform: scale(1);
  }
`;

const bounceOut = keyframes`
  from {
    transform: scale(1);
  }
  15% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.15);
  }
  to {
    transform: scale(0);
  }
`;

const shine = keyframes`
  from {
    transform: rotate(45deg) translateX(-100%);
  }
  30% {
    transform: rotate(45deg) translateX(120%);
  }
  to {
    transform: rotate(45deg) translateX(120%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(15%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Root = styled(ButtonBase)`
  z-index: ${zIndexes.outerContext.aboveCard};
  cursor: pointer;
  border: none;
  background: transparent;
  border-radius: 50%;
  box-shadow: -6px 12px 20px rgba(0, 0, 0, 0.3);
  --click-animation-duration: 1s;
  --bounce-duration: 2s;
  animation: ${fadeIn} var(--click-animation-duration);
  &:not(.disablePositioning) {
    isolation: isolate;
    position: absolute;
    top: calc(-1 * var(--size) / 2);
    left: calc(-1 * var(--size) / 2);
  }
`;
const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: var(--c-nd-800-opaque);
  top: 0;
  left: 0;
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    content: '';
    border-radius: 50%;
  }
  &::before {
    z-index: ${zIndexes.innerContext.belowLetter1};
    background-color: var(--c-nd-800-opaque);
  }
  &::after {
    z-index: ${zIndexes.innerContext.belowLetter2};
    background-color: var(--c-rewards-pink);
    transform: scale(0);
  }
  &.bounce {
    animation: ${bounce} var(--bounce-duration) ease infinite;
  }
  &.active {
    background-color: transparent;
    animation: none;
    &::before {
      animation: ${bounceOut} var(--click-animation-duration) ease forwards;
    }
    &::after {
      animation: ${bounceIn} var(--click-animation-duration) ease forwards;
    }
  }
  &.deselected {
    &::before {
      z-index: ${zIndexes.innerContext.belowLetter2};
      transform: scale(0);
      animation: ${bounceIn} var(--click-animation-duration) ease forwards;
    }
    &::after {
      z-index: ${zIndexes.innerContext.belowLetter1};
      animation: ${bounceOut} var(--click-animation-duration) ease forwards;
    }
  }
`;
const CaptainWrapper = styled.div`
  position: relative;
  z-index: ${zIndexes.innerContext.letter};
`;
const ShineWrapper = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: ${zIndexes.innerContext.aboveLetter};
  background: transparent;
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;
  &::after {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.9),
      transparent
    );
    border-radius: 50%;
    transform: rotate(45deg) translateX(-100%);
  }
  &.shine::after {
    animation: ${shine} var(--bounce-duration) infinite;
  }
  &.shineDeselected::after {
    animation: ${shine} var(--bounce-duration) var(--bounce-duration) infinite;
  }
`;

export const CaptainToggle = ({
  size = 3,
  active,
  onClick = () => {},
  disablePositioning,
  disableAnimation,
}: Props) => {
  const [clicked, setClicked] = useState(false);
  const onCaptainClick = () => {
    setClicked(true);
    onClick();
  };

  return (
    <Root
      type="button"
      onClick={onCaptainClick}
      className={classnames({ disablePositioning })}
      style={{ '--size': unitMapping[size] } as CSSProperties}
    >
      <Background
        className={classnames(
          { bounce: !disableAnimation },
          { active },
          { deselected: !active && clicked }
        )}
      />
      <CaptainWrapper>
        <Captain active={active} size={size} />
      </CaptainWrapper>
      <ShineWrapper
        className={classnames({
          shine: !active && !clicked && !disableAnimation,
          shineDeselected: !active && clicked && !disableAnimation,
        })}
      />
    </Root>
  );
};
